import { useNavigate } from 'react-router';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { practiceService } from '@/core/services/practice';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import type {
  ImportExceptionRow,
  ImportExceptionStatus,
  ImportExceptionSummary,
  ImportSummaryRow,
} from '@/core/models/phase1.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import {
  formatDateTime,
  importBatchStatusName,
  periodDash,
  SearchField,
  fmt2,
  useAlive,
  useClientInit,
  useLive,
} from './common';
import { ResolveExceptionDialog, type ResolveExceptionDialogData } from './ResolveExceptionDialog';

const ALL = -1;
/** Mirrors tr-core/trialBalanceEditor.ts's TB_LIST_ROUTE - feature folders here don't cross-import page constants. */
const TB_LIST_ROUTE = '/home/taxreturns/tasks-trialbalance';

const STATUS_OPTIONS = [
  { id: ALL, name: 'All' },
  { id: 0, name: 'Open' },
  { id: 1, name: 'Resolved' },
  { id: 2, name: 'Ignored' },
];

interface Model {
  /** APT334: the client of the import - Add to chart creates the account in this client's chart. */
  clientId: string | null;
  dataSource: ImportExceptionRow[];
  periods: PeriodRow[];
  selectedPeriodId: string | null;
  imports: ImportSummaryRow[];
  selectedImportId: string | null;
  statusFilter: number;
  searchText: string;
  summary: ImportExceptionSummary | null;
}

const statusName = (status: ImportExceptionStatus) => (status === 0 ? 'Open' : status === 1 ? 'Resolved' : 'Ignored');

/** ImportStatus Staged: the only batch Re-check and Resume Import can act on (the API refuses the rest: 9107/9109/9111). */
const STAGED = 0;

/**
 * APT334 (the client's answer: "only that particular line should wait"): a live batch whose import was committed with some lines held back
 * (an account the chart lacks, a reserved code). The rest is posted, so the adjusted trial balance is out of balance by what they come to until
 * each is mapped, added to the chart, or corrected on the Trial Balance and saved again.
 */
function partlyPostedMessage(heldLineCount: number, heldNet: number): string {
  const side = heldNet >= 0 ? 'Dr' : 'Cr';
  return (
    `This import is live but partly posted: ${heldLineCount} line(s) held back (${fmt2(Math.abs(heldNet))} ${side}). ` +
    `The rest is posted, so the adjusted trial balance is out of balance by ${fmt2(Math.abs(heldNet))} until each is mapped to a chart account ` +
    'or its account is added to the chart. Resolve them below.'
  );
}

/** APT334: why Re-check and Resume Import are off for a batch that is not Staged. */
function importNotActionableMessage(status: number): string {
  if (status === 1) return 'This import is already committed and live for this period. No further action is needed.';
  if (status === 2) return 'This import was replaced by a newer import.';
  return 'This import failed and cannot be resumed. Import the trial balance again.';
}

const importedAt = (row: ImportSummaryRow) => {
  const t = Date.parse(row.importedAtUtc);
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
};

/** The first of `status` by import time (newest); batches without a time keep the list order. */
function newestWithStatus(imports: ImportSummaryRow[], status: number): ImportSummaryRow | undefined {
  let best: ImportSummaryRow | undefined;
  for (const row of imports) {
    if (row.status === status && (!best || importedAt(row) > importedAt(best))) best = row;
  }
  return best;
}

/** APT334 default: the newest Staged batch, else the live (newest Committed) one, else the first in the list. */
function pickDefaultImport(imports: ImportSummaryRow[]): ImportSummaryRow | null {
  return newestWithStatus(imports, STAGED) ?? newestWithStatus(imports, 1) ?? imports[0] ?? null;
}

/** APT334: a live batch that holds lines back says how many in its label. */
const heldLabel = (row: ImportSummaryRow) => (row.status === 1 && (row.heldLineCount ?? 0) > 0 ? `, ${row.heldLineCount} held` : '');

/** Totals are shown only when the batch has some (a failed or empty batch has none worth printing). */
const batchTotals = (row: ImportSummaryRow) =>
  row.totalDebit !== 0 || row.totalCredit !== 0 ? ` · Dr ${fmt2(row.totalDebit)} / Cr ${fmt2(row.totalCredit)}` : '';

/**
 * AP-003 exception queue: the rows an import could not accept, resolved one at a time, and the resumable commit.
 * (Angular TaxreturnImportexceptionsComponent.)
 */
export function TaxreturnImportexceptions() {
  const alive = useAlive();
  const navigate = useNavigate();
  const [m, set, ref] = useLive<Model>({
    clientId: null,
    dataSource: [],
    periods: [],
    selectedPeriodId: null,
    imports: [],
    selectedImportId: null,
    statusFilter: ALL,
    searchText: '',
    summary: null,
  });

  const load = async () => {
    const s = ref.current;
    if (!s.selectedPeriodId) {
      return;
    }
    try {
      const page = await phase1Service.getExceptions(s.selectedPeriodId, {
        importBatchId: s.selectedImportId,
        status: s.statusFilter === ALL ? null : (s.statusFilter as ImportExceptionStatus),
        search: s.searchText?.trim() || null,
        pageNumber: 1,
        pageSize: 500,
      });
      if (!alive.current) return;
      set({ dataSource: page.items ?? [], summary: page.summary });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const loadImports = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const rows = await phase1Service.getImports(periodId);
      if (!alive.current) return;
      const imports = rows ?? [];
      // Default to the newest batch that has not been committed yet, else the live committed one.
      set({ imports, selectedImportId: pickDefaultImport(imports)?.importBatchId ?? null });
    } catch (error) {
      if (alive.current) notifyError(error);
      return;
    }
    await load();
  };

  useClientInit(async (client) => {
    try {
      const response = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = response.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const selectedPeriodId = await resolveDefaultPeriodId(client.id, periods);
      set({ clientId: client.id, periods, selectedPeriodId });
      if (selectedPeriodId) {
        await loadImports();
      } else {
        notifyError('Add an accounting period before importing a trial balance.');
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  const onPeriodChange = (periodId: string | null) => {
    rememberChosenPeriod(periodId);
    set({ selectedPeriodId: periodId, selectedImportId: null, imports: [], dataSource: [], summary: null });
    void loadImports();
  };

  /** Re-runs the import's validation rules and rebuilds the open queue. */
  const onValidate = async () => {
    const s = ref.current;
    if (!s.selectedPeriodId || !s.selectedImportId) {
      notifyError('Select an import batch first.');
      return;
    }
    try {
      const result = await phase1Service.validateImport(s.selectedPeriodId, s.selectedImportId);
      if (!alive.current) return;
      notifySuccess(
        result.openExceptionCount === 0
          ? 'No exceptions outstanding. The import is ready to commit.'
          : `${result.openExceptionCount} exception(s) need attention.`,
      );
      void load();
      // Re-check recomputes ap.ImportBatch's totals server-side; refresh the dropdown's rows (for the
      // balance banner) without disturbing which batch is currently selected the way loadImports() would.
      void phase1Service.getImports(s.selectedPeriodId).then((rows) => {
        if (alive.current) set({ imports: rows ?? [] });
      });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** Commits the corrected batch, or reports what is still outstanding. */
  const onResume = async () => {
    const s = ref.current;
    if (!s.selectedPeriodId || !s.selectedImportId) {
      notifyError('Select an import batch first.');
      return;
    }
    try {
      const result = await phase1Service.resumeImport(s.selectedPeriodId, s.selectedImportId);
      if (!alive.current) return;
      if (result.committed) {
        const committed = `Import committed: ${result.linesCommitted} line(s), ${result.mappingsApplied} mapping(s) applied.`;
        // APT334: the lines it left held back (an account the chart lacks) are named, with what the trial balance is out by.
        notifySuccess(
          result.heldLines ? `${committed} ${result.message ?? `${result.heldLines} line(s) are held on Import Exceptions.`}` : committed,
        );
      } else {
        notifyError(
          `Resolve the remaining ${result.openExceptionCount} exception(s) before the import can be committed.`,
        );
      }
      void loadImports();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const onResolve = (row: ImportExceptionRow) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }

    if (row.status !== 0) {
      return;
    }
    const live = ref.current.imports.find((i) => i.importBatchId === ref.current.selectedImportId)?.status === 1;
    const data: ResolveExceptionDialogData = { periodId, exception: row, clientId: ref.current.clientId, batchCommitted: live };

    void openDialog(ResolveExceptionDialog, { disableClose: true, panelClass: 'cst-invite', data })
      .afterClosed()
      .then((resolved) => {
        if (resolved) {
          // APT334: a line of a live batch that is now right is posted into it straight away.
          const posted = typeof resolved === 'object' ? (resolved.linesPosted ?? 0) : 0;
          notifySuccess(posted > 0 ? `Exception resolved. ${posted} line(s) posted into the live import.` : 'Exception resolved.');
          void load();
          if (live) {
            // The held count on the batch list (and its label) has changed.
            void phase1Service.getImports(periodId).then((rows) => {
              if (alive.current) set({ imports: rows ?? [] });
            });
          }
        }
      });
  };

  const columns: DataTableColumn<ImportExceptionRow>[] = [
    { id: 'row', header: 'Row', cell: (element) => element.rowNumber },
    { id: 'code', header: 'Nominal', cell: (element) => element.code },
    { id: 'description', header: 'Description', cell: (element) => element.description },
    { id: 'debit', header: 'Debit', cell: (element) => fmt2(element.debit) },
    { id: 'credit', header: 'Credit', cell: (element) => fmt2(element.credit) },
    {
      id: 'error',
      header: 'Exception',
      cell: (element) => (
        <div
          className={`d-flex align-items-center gap-5 ${element.severity === 'Blocking' ? 'text-red' : 'text-blue'}`}
        >
          <Icon name={element.severity === 'Blocking' ? 'error' : 'warning'} />
          {/* APT334: severity is text as well as colour. */}
          <strong>{element.severity === 'Blocking' ? 'Blocking' : 'Warning'}</strong>
          <span>{element.errorMessage}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (element) => (
        <span className={element.status === 0 ? 'text-red' : 'text-green'}>{statusName(element.status)}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          {element.status === 0 && (
            <Button title="Resolve" onClick={() => onResolve(element)}>
              <Icon name="build" outlined />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const selectedImport = m.imports.find((i) => i.importBatchId === m.selectedImportId) ?? null;
  const difference = selectedImport ? selectedImport.totalDebit - selectedImport.totalCredit : 0;
  // Only a Staged batch can still be corrected; an older or failed batch's totals are history, not a call to action.
  const isStaged = !selectedImport || selectedImport.status === STAGED;
  const isUnbalanced = !!selectedImport && selectedImport.status === STAGED && Math.abs(difference) >= 0.005;
  // APT334: a live batch that still holds lines back is partly posted - say how many and how far out the adjusted trial balance is.
  const heldLineCount = m.summary?.heldLineCount ?? 0;
  const heldNet = (m.summary?.heldDebit ?? 0) - (m.summary?.heldCredit ?? 0);
  const isPartlyPosted = !!selectedImport && selectedImport.status === 1 && heldLineCount > 0;
  const notActionableMessage =
    selectedImport && !isStaged
      ? isPartlyPosted
        ? partlyPostedMessage(heldLineCount, heldNet)
        : importNotActionableMessage(selectedImport.status)
      : null;

  return (
    <SideMenuPage menu="task">
      <div className="content-body">
        {/* APT346: Import Exceptions is opened from the Trial Balance screen, which is the way back (the side menu keeps Trial Balance highlighted). */}
        <div className="d-flex align-items-center gap-5 cursor-pointer mb-10" onClick={() => navigate(TB_LIST_ROUTE)}>
          <Icon name="arrow_back" className="text-primary" />
          <span className="link">Back to Trial Balance</span>
        </div>
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Accounting period"
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              value={m.selectedPeriodId}
              onChange={(value) => onPeriodChange((value as string | null) ?? null)}
              renderLabel={periodDash}
              renderOption={periodDash}
            />

            <Select
              className="w-300"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Import batch"
              items={m.imports}
              bindLabel="sourceType"
              bindValue="importBatchId"
              clearable={false}
              value={m.selectedImportId}
              onChange={(value) => {
                set({ selectedImportId: (value as string | null) ?? null });
                void load();
              }}
              renderLabel={(item) =>
                `${item.sourceType} - ${item.lineCount} lines (${importBatchStatusName(item.status)}${heldLabel(item)})${batchTotals(item)}`
              }
              renderOption={(item) => {
                const when = formatDateTime(item.importedAtUtc);
                return `${item.sourceType} - ${item.lineCount} lines · ${importBatchStatusName(item.status)}${heldLabel(item)}${batchTotals(item)}${when ? ` · ${when}` : ''}`;
              }}
            />

            <SearchField
              value={m.searchText}
              onValueChange={(searchText) => set({ searchText })}
              onCommit={() => void load()}
            />
          </div>

          <div className="content-header-right mb-1">
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={STATUS_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.statusFilter}
              onChange={(value) => {
                set({ statusFilter: value as number });
                void load();
              }}
            />
            <Button
              className="action-button secondary-button"
              disabled={!isStaged}
              title={notActionableMessage ?? undefined}
              onClick={() => void onValidate()}
            >
              <Icon name="fact_check" outlined />
              <span>Re-check</span>
            </Button>
            <Button
              className="action-button primary-button"
              disabled={!isStaged || isUnbalanced}
              title={
                notActionableMessage ??
                (isUnbalanced ? 'The trial balance does not balance. Correct it and Save it again.' : undefined)
              }
              onClick={() => void onResume()}
            >
              <Icon name="play_arrow" outlined />
              <span>Resume Import</span>
            </Button>
          </div>
        </div>

        {notActionableMessage && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <span role="status" className={isPartlyPosted ? 'text-red' : undefined}>
                {notActionableMessage}
              </span>
            </div>
          </div>
        )}

        {isUnbalanced && selectedImport && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <span className="text-red">
                This trial balance does not balance &middot; Debit {fmt2(selectedImport.totalDebit)} &middot; Credit{' '}
                {fmt2(selectedImport.totalCredit)} &middot; Difference {fmt2(Math.abs(difference))}
              </span>
              <Button className="action-button secondary-button ml-1" onClick={() => navigate(TB_LIST_ROUTE)}>
                <span>Go to Trial Balance</span>
              </Button>
            </div>
          </div>
        )}

        {m.summary && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              {m.summary.openCount > 0 && (
                <span className="text-red">
                  {m.summary.openCount} open ({m.summary.blockingCount} blocking)
                </span>
              )}
              {isUnbalanced && m.summary.openCount > 0 && <span>&nbsp;&middot;&nbsp;</span>}
              {isUnbalanced && (
                <span className="text-red">
                  Out of balance by {fmt2(Math.abs(difference))}: correct the trial balance and Save it again
                </span>
              )}
              {m.summary.openCount === 0 && !isUnbalanced && <span className="text-green">No open exceptions</span>}
              {m.summary.openCount === 0 && !isUnbalanced && !!selectedImport && selectedImport.status === STAGED && (
                <span className="text-green">&nbsp;&middot;&nbsp;Ready to commit</span>
              )}
              <span>
                &nbsp;&middot;&nbsp;{m.summary.resolvedCount} resolved &middot; {m.summary.ignoredCount} ignored
              </span>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={m.dataSource} rowKey={(row) => row.importExceptionId} stickyHeader />
        </div>
      </div>
    </SideMenuPage>
  );
}
